"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertCircle, ChevronDown } from "lucide-react";
import { useTokenStore } from "@/lib/zustand";
import { useAllFeaturedProjects } from "@/hooks/use-featured-projects";
import { ChainType, ChainKey, Token, getToken } from "@lifi/sdk";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSupportedChains } from "@/hooks/use-supported-chains";
import { zeroAddress } from "viem"; 
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useParaAccount } from "@/hooks/use-appkit-account";

export const FeaturedProjectsDropdown = () => {
  const { setToToken, setToChain, setFromToken, setFromChain, setFromTokenAmount } =
    useTokenStore();
  const { data: projects, isLoading, error } = useAllFeaturedProjects();
  const { data: allChains } = useSupportedChains({
    search: "",
  });
  const { caipAddress, isConnected } = useParaAccount();
  const [showWalletAlert, setShowWalletAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [isLoadingTimedOut, setIsLoadingTimedOut] = useState(false);

  // Use projects directly - AvatarFallback handles missing logos
  const formattedProjects = projects?.formattedProjects ?? [];

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingTimedOut(false);
      return;
    }

    const timeout = setTimeout(() => {
      setIsLoadingTimedOut(true);
      console.warn(
        "[FeaturedProjectsDropdown] Featured projects query exceeded 5s timeout. Rendering fallback state."
      );
    }, 5000);

    return () => clearTimeout(timeout);
  }, [isLoading]);

  const handleProjectSelect = async (token: Token | null) => {
    try {
      // Get the token details
      if (!token) {
        toast.error("Token not found");
        return;
      }

      const thisChain = allChains?.chains.find((c) => c.id === token?.chainId);

      if (!thisChain) {
        toast.error("Chain not found");
        return;
      }

      // Check wallet compatibility
      if (isConnected && caipAddress) {
        const isSVMConnected = caipAddress.startsWith("solana:");
        const requiredChainType = thisChain.chainType;

        // if (requiredChainType === ChainType.SVM && !isSVMConnected) {
        //   setAlertMessage(
        //     "Please switch to a Solana-compatible wallet to swap on Solana."
        //   );
        //   setShowWalletAlert(true);
        //   return;
        // }

        // if (requiredChainType === ChainType.EVM && isSVMConnected) {
        //   setAlertMessage(
        //     "Please switch to an EVM-compatible wallet to swap on EVM chains."
        //   );
        //   setShowWalletAlert(true);
        //   return;
        // }

        // Network switching is handled by useAutoSwitchChain after state updates
      }

      // Set the chain
      const chain = {
        id: token.chainId,
        name: thisChain.name ?? "",
        logoURI: thisChain.logoURI ?? "",
        chainType: thisChain.chainType ?? ChainType.EVM,
        symbol: thisChain.symbol as ChainKey,
      };

      // Set both chains to the same chain
      setToChain(chain);
      setFromChain(chain);

      // Set the toToken to the selected token
      setToToken(token);

      // Set the fromToken to the native token of the chain
      const nativeToken = await getToken(
        token.chainId,
        thisChain.chainType === ChainType.SVM
          ? "11111111111111111111111111111111" // Solana native token address
          : zeroAddress // EVM native token address
      );
      setFromToken(nativeToken);

      // Reset the amount when changing tokens to avoid stale amounts from previous token
      // being interpreted as the new token (e.g., 94309 SPL tokens becoming 94309 SOL)
      if (process.env.NODE_ENV !== "production") {
        console.log("[Featured Dropdown] Resetting fromTokenAmount to 0");
      }
      setFromTokenAmount("0");
    } catch (error) {
      toast.error("Error selecting project", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  if (isLoading && !isLoadingTimedOut) {
    return (
      <Button
        variant="ghost"
        className="flex items-center justify-between w-full sm:w-auto min-w-[220px] rounded-[8px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.07)] text-[#8db4d6] text-[12px] font-semibold px-[14px] py-[6px]"
        disabled
      >
        Loading featured tokens...
        <ChevronDown className="w-4 h-4 text-[#999] text-[10px]" />
      </Button>
    );
  }

  if (isLoadingTimedOut) {
    return (
      <Button
        variant="ghost"
        className="flex items-center justify-between w-full sm:w-auto min-w-[220px] rounded-[8px] bg-[rgba(255,255,255,0.03)] border border-[rgba(255,184,0,0.25)] text-[#f0c76a] text-[12px] font-semibold px-[14px] py-[6px]"
        disabled
        title="Featured tokens are taking longer than expected to load"
      >
        <span className="inline-flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5" />
          Featured Tokens (Delayed)
        </span>
        <ChevronDown className="w-4 h-4 text-[#c8a24b] text-[10px]" />
      </Button>
    );
  }

  if (error) {
    return (
      <Button
        variant="ghost"
        className="flex items-center justify-between w-full sm:w-auto min-w-[220px] rounded-[8px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.07)] text-[#ccc] text-[12px] font-semibold px-[14px] py-[6px] hover:bg-[rgba(255,255,255,0.06)]"
      >
        Error loading projects
        <ChevronDown className="w-4 h-4 text-[#999] text-[10px]" />
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="flex items-center justify-between w-full sm:w-auto min-w-[220px] rounded-[8px] bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.07)] text-[#ccc] text-[12px] font-semibold px-[14px] py-[6px] hover:bg-[rgba(255,255,255,0.06)] transition-all duration-200 ease-out"
          >
            FEATURED TOKENS
            <ChevronDown className="w-4 h-4 text-[#999] text-[10px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="!min-w-80 sm:w-56 max-h-96 overflow-y-auto border-white/10 bg-[#141414]">
          <DropdownMenuGroup className="!cursor-pointer">
            {formattedProjects?.map((project, index) => {
              const thisChain = allChains?.chains.find(
                (c) => c.id === project?.chainId
              );
              const chainImage = thisChain?.logoURI;
              return (
                <DropdownMenuItem
                  key={index}
                  onClick={() => handleProjectSelect(project)}
                  className="flex items-center gap-2 justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Avatar>
                      <AvatarImage src={project?.logoURI} />
                      <AvatarFallback>
                        {project?.symbol.slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <span>{project?.symbol}</span>
                  </div>
                  <Avatar className="w-6 h-6">
                    <AvatarImage src={chainImage} />
                    <AvatarFallback>
                      {thisChain?.name.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showWalletAlert} onOpenChange={setShowWalletAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Wallet Type Mismatch</AlertDialogTitle>
            <AlertDialogDescription>{alertMessage}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowWalletAlert(false)}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
